import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import NearbyServiceAreas from '@/components/common/NearbyServiceAreas';

const DentalImplants = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Dental Implants Fort Worth TX | Expert Implant Dentist | Fort Worth Dental</title>
        <meta name="description" content="Expert dental implants in Fort Worth by Dr. Mund, Diplomate of American Board of Oral Implantology. Natural-looking tooth replacement. FREE consultation. Call (817) 985-3337." />
        <meta name="keywords" content="dental implants fort worth, dental implant dentist, tooth replacement fort worth tx, implant dentistry, missing teeth solution, dental implant cost fort worth" />
        <link rel="canonical" href="https://fortworthdental.com/dental-implants" />
        <meta property="og:url" content="https://fortworthdental.com/dental-implants" />
        <meta property="og:title" content="Dental Implants Fort Worth TX | Fort Worth Dental" />
        <meta property="og:description" content="Expert dental implants by Dr. Mund. Natural-looking tooth replacement with 25+ years experience." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Dental Implants</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Transform Your Smile with Natural Dental Implants
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Why Choose Implants */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img
                  src="/images/services/frame-1279-4.png"
                  alt="Dental implant procedure diagram showing titanium implant post, abutment, and porcelain crown replacing missing tooth in Fort Worth TX"
                  className="rounded-lg shadow-lg w-full"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Why Would I Want Dental Implants?
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Dental implants are one of those procedures that can change your smile and change your life! They are the perfect option for those who may be missing a tooth or several teeth. Replacing those missing teeth with natural looking and comfortable feeling teeth can bring a major increase in confidence.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Many patients who were once embarrassed to smile, laugh, or eat in public and social situations find that they no longer need to stay out of the spotlight.
                </p>
              </div>
            </div>
          </section>

          {/* Advantages */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Are The Advantages Of Dental Implants?
                </h2>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">✓</span>
                    <span><strong>Long-lasting:</strong> Dental implants last for a long time, providing long-term support for your jawbone.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">✓</span>
                    <span><strong>Comfortable:</strong> Dental implants feel more comfortable and natural than dentures.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">✓</span>
                    <span><strong>Natural Looking:</strong> Dental implants look as natural as real teeth.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">✓</span>
                    <span><strong>Preserve Jawbone:</strong> Implants help maintain your facial structure and prevent bone loss.</span>
                  </li>
                </ul>
              </div>
              <div>
                <img
                  src="/images/services/frame-1279-5.png"
                  alt="Benefits of dental implants - Before and after comparison showing natural-looking tooth replacement results at Fort Worth Dental"
                  className="rounded-lg shadow-lg w-full"
                />
              </div>
            </div>
          </section>

          {/* Who Needs Implants */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img
                  src="/images/services/frame-1279-6.png"
                  alt="Dental implant candidate consultation - Patient discussing missing teeth replacement options with Dr. Mund at Fort Worth Dental"
                  className="rounded-lg shadow-lg w-full"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Who Needs Dental Implants?
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Any patient who may be missing one or more teeth is candidate to consider dental implants. Whether you are missing teeth because of gum disease, an injury, or tooth decay, dental implants can make the difference for you.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Most patients testify of results that are overwhelming and life-changing after benefiting from dental implants. The most common benefits are increased confidence to interact with others in public settings.
                </p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-12 rounded-lg text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-8">
              Schedule your FREE consultation today and discover how dental implants can transform your smile!
            </p>
            <div className="flex flex-col md:flex-row justify-center gap-4">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 inline-block"
              >
                Call (817) 985-3337
              </a>
              <Link
                to="/implant-center"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700 inline-block"
              >
                Visit Our Implant Center
              </Link>
            </div>
          </section>
        </div>
      </div>

      {/* Nearby Cities - SEO Section */}
      <NearbyServiceAreas serviceSlug="dental-implants" serviceName="Dental Implants" />
    </div>
  );
};

export default DentalImplants;